import type { NearbyTreeApi } from "@/lib/api";

interface NearbyTreeListProps {
  trees: NearbyTreeApi[];
  loading: boolean;
  radiusM: number;
  onOpenTree: (treeId: number) => void;
}

function formatDistance(meters: number): string {
  return meters < 10
    ? `${meters.toFixed(2).replace(".", ",")} m`
    : `${meters.toFixed(1).replace(".", ",")} m`;
}

/**
 * Possible duplicates near the chosen point.
 *
 * This only warns. Proximity never merges records and never blocks the
 * registration: two trunks can legitimately stand a metre apart, and only the
 * operator on site can tell that from a duplicate.
 */
export default function NearbyTreeList({
  trees,
  loading,
  radiusM,
  onOpenTree,
}: NearbyTreeListProps) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-foreground-500 py-3">
        <i className="ri-loader-4-line animate-spin" />
        Verificando exemplares próximos...
      </div>
    );
  }

  if (trees.length === 0) {
    return (
      <div className="flex items-start gap-2 rounded-lg bg-[#EAF5EF] border border-[#C7E3D4] px-3 py-2.5">
        <i className="ri-checkbox-circle-line text-primary-600 mt-0.5" />
        <p className="text-sm text-[#2D4A3A]">
          Nenhum exemplar cadastrado num raio de {radiusM} m desta posição.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-[#F0D9A8] bg-[#FDF7EA] overflow-hidden">
      <div className="flex items-start gap-2 px-3 py-2.5 border-b border-[#F0D9A8]">
        <i className="ri-alert-line text-[#B07D1A] mt-0.5" />
        <div>
          <p className="text-sm font-medium text-[#6B4E12]">
            {trees.length === 1
              ? "1 exemplar já cadastrado por perto"
              : `${trees.length} exemplares já cadastrados por perto`}
          </p>
          <p className="text-xs text-[#8A6A2F] mt-0.5">
            Confira se não é o mesmo indivíduo antes de cadastrar. Nada é unificado
            automaticamente.
          </p>
        </div>
      </div>

      <ul className="divide-y divide-[#F0D9A8]">
        {trees.map((tree) => (
          <li key={tree.id} className="flex items-center gap-3 px-3 py-2.5">
            <span className="w-2.5 h-2.5 rounded-full bg-[#E8A33D] shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground-900 truncate">
                {tree.cta_code}
              </p>
              <p className="text-xs text-foreground-500 truncate">
                {tree.species_popular_name || "Espécie não identificada"}
                {tree.species_scientific_name ? ` · ${tree.species_scientific_name}` : ""}
              </p>
            </div>
            <span className="text-xs font-mono text-[#8A6A2F] shrink-0">
              {formatDistance(tree.distance_m)}
            </span>
            <button
              type="button"
              onClick={() => onOpenTree(tree.id)}
              className="text-xs font-medium text-primary-600 hover:underline shrink-0"
            >
              Abrir
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
